import React, { useEffect, useState } from "react";
import { getTheme, IScrollablePaneStyles, mergeStyleSets, ScrollablePane } from "@fluentui/react";

import {
  ItemRenderProps,
  TreeView,
  TreeViewExpandChangeEvent,
  processTreeViewItems,
  TreeViewItemClickEvent,
} from "@progress/kendo-react-treeview";
import "@progress/kendo-theme-bootstrap/dist/all.css";
import LoadingManager from "../../common/loading/LoadingManager";
import { FORM_TREE_NOTIFICATION } from "../../utils/constants/notification";
import { fetchFileNode, ExcelFile } from "../../api/object/ExcelFile";

export interface FormTreeSelection {
  dbId: string;
  formCode: string;
  formDesc: string;
  key: string;
  oidPath: string[];
}
export interface IFileTreeProps {
  onFileClick?: (selection: FormTreeSelection) => void;
  oidPath?: string[];
}

interface TreeState {
  dbIdLoaded: string;
  treeItems: TreeViewDataItem[];
  selectState: string[];
}

interface TreeViewDataItem {
  text: string;
  key: string;
  hasChildren: boolean;
  dbId: string;
  nodeType: "form" | "node";
  formCode: string;
  formDesc: string;
  expanded?: boolean;
  checked?: boolean;
  selected?: boolean;
  items?: TreeViewDataItem[];
}

/**
 * Ultis functions
 */
function mapFileToDataItem(formFile: ExcelFile): TreeViewDataItem {
  const item: TreeViewDataItem = {
    dbId: "",
    text: formFile.desc,
    key: formFile.oid,
    hasChildren: true,
    nodeType: formFile.type,
    formCode: formFile.code,
    formDesc: formFile.desc,
    expanded: false,
  };
  if (formFile.type === "form") {
    item.hasChildren = false;
    item.expanded = true;
    item.items = [];
  }
  return item;
}

const ItemRenderer = (props: ItemRenderProps) => {
  return (
    <>
      <span className={iconClassName(props.item)} key="0" />
      {props.item.text}
    </>
  );
};

function iconClassName(item: TreeViewDataItem) {
  if (item.nodeType === "node") {
    return "k-icon k-i-folder";
  } else {
    return "k-icon k-i-excel";
  }
}

export const FileTree: React.FunctionComponent<IFileTreeProps> = (props) => {
  // state hooks
  const [state, setState] = useState<TreeState>({ dbIdLoaded: "", treeItems: [], selectState: [] });

  const copyItemsAndItem = (
    items: TreeViewDataItem[],
    indexes: number[]
  ): { items: TreeViewDataItem[]; item: TreeViewDataItem } => {
    const copiedItems = items.slice();
    const item = (copiedItems[indexes[0]] = { ...copiedItems[indexes[0]] });

    if (indexes.length > 1) {
      const { items: children, item: target } = copyItemsAndItem(item.items, indexes.slice(1));
      item.items = children;
      return { items: copiedItems, item: target };
    } else {
      return { items: copiedItems, item: item };
    }
  };

  const treePath = (items: TreeViewDataItem[], indexes: number[]): TreeViewDataItem[] => {
    const item = items[indexes[0]];
    if (item.items.length > 0) {
      return [item, ...treePath(item.items, indexes.slice(1))];
    } else {
      return [item];
    }
  };

  useEffect(() => {
    LoadingManager.create(FORM_TREE_NOTIFICATION);
    fetchFileNode().then((dataFiles) => {
      let roots = dataFiles.map((formFile) => mapFileToDataItem(formFile));
      if (props.oidPath && props.oidPath.length > 0) {
        // eslint-disable-next-line no-undef
        const oidPath = Array.isArray(props.oidPath) ? props.oidPath : [props.oidPath];
        // eslint-disable-next-line no-undef
        console.log(`provided oidPath ${props.oidPath} ... `);
        retrieveSelectedData(oidPath, roots).then(({ rootsWithPath, itemHierarchicalIndex }) =>
          setState({
            dbIdLoaded: "",
            treeItems: rootsWithPath,
            selectState: [itemHierarchicalIndex],
          })
        );
      } else {
        setState({
          dbIdLoaded: "",
          treeItems: roots,
          selectState: [],
        });
      }
      LoadingManager.remove(FORM_TREE_NOTIFICATION);
    });
    return () => LoadingManager.remove(FORM_TREE_NOTIFICATION);
  }, ["dbId"]);

  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    const indexes = event.itemHierarchicalIndex.split("_").map((value) => Number(value));
    const { items, item } = copyItemsAndItem(state.treeItems, indexes);
    item.expanded = !item.expanded;
    setState({ ...state, treeItems: items });
    if (!item.items) {
      fetchFileNode(item.key).then((dataForms) => {
        const { items: children, item: parent } = copyItemsAndItem(items, indexes);
        parent.items = dataForms.map((formFile) => mapFileToDataItem(formFile));
        parent.hasChildren = false; // hasChildren is need only for lazy loading
        setState({ ...state, treeItems: children });
      });
    }
  };

  const onItemClick = (event: TreeViewItemClickEvent) => {
    // eslint-disable-next-line no-undef
    console.log("ITEMCLICK", event);
    if (event.item.nodeType === "form") {
      setState({ ...state, selectState: [event.itemHierarchicalIndex] });

      if (props.onFileClick) {
        props.onFileClick({
          dbId: "",
          formCode: event.item.formCode,
          formDesc: event.item.formDesc,
          key: event.item.key,
          oidPath: treePath(
            state.treeItems,
            event.itemHierarchicalIndex.split("_").map((value) => Number(value))
          ).map((item) => item.key),
        });
      }
    }
  };

  return (
    <div>
      <TreeView
        data={processTreeViewItems(state.treeItems, { select: state.selectState })}
        expandIcons={true}
        onExpandChange={onExpandChange}
        item={ItemRenderer}
        onItemClick={onItemClick}
      />
    </div>
  );
};

export const ScrollableFileTree: React.FunctionComponent<IFileTreeProps> = (props) => {
  const theme = getTheme();
  const classNames = mergeStyleSets({
    wrapper: {
      height: "70vh",
      position: "relative",
      maxHeight: "inherit",
    },
    pane: {
      // maxWidth: 400,
      border: "1px solid " + theme.palette.neutralLight,
    },
  });
  const scrollablePaneStyles: Partial<IScrollablePaneStyles> = { root: classNames.pane };

  return (
    <div className={classNames.wrapper}>
      <ScrollablePane styles={scrollablePaneStyles}>
        <FileTree {...props} />
      </ScrollablePane>
    </div>
  );
};

function retrieveSelectedData(
  oidPath: string[],
  roots: TreeViewDataItem[]
): Promise<{ rootsWithPath: TreeViewDataItem[]; itemHierarchicalIndex: string }> {
  const data: Map<string, ExcelFile[]> = new Map();
  const nodeOidPath = oidPath.slice(0, oidPath.length - 1);
  const formOid = oidPath[oidPath.length - 1];
  const promises: Promise<ExcelFile[]>[] = nodeOidPath.map((oid) => {
    const promise = fetchFileNode(oid);
    promise.then((forms) => data.set(oid, forms));
    return promise;
  });
  return Promise.all(promises).then(() => {
    let items = roots;
    const hierarchicalIndexArray: number[] = [];
    nodeOidPath.forEach((oid) => {
      const children = data.get(oid).map((form) => mapFileToDataItem(form));
      const fatherIndex = items.findIndex((value) => value.key === oid);
      const father = items[fatherIndex];
      hierarchicalIndexArray.push(fatherIndex);
      father.expanded = true;
      father.items = children;
      items = children;
    });

    // getting form index
    const formIndex = items.findIndex((value) => value.key === formOid);
    if (formIndex >= 0) {
      hierarchicalIndexArray.push(formIndex);
    } else {
      hierarchicalIndexArray.splice(0);
      // eslint-disable-next-line no-undef
      console.warn("formIndex not found in items (-1). No node will be selected.");
    }
    return { rootsWithPath: roots, itemHierarchicalIndex: hierarchicalIndexArray.join("_") };
  });
}
